import React from 'react';
import { Divider } from '@material-ui/core';
import { TrendSummaryConfig } from 'configuration-schemas/viewdefns/viewdefn';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { calcTrendCardWidth } from 'src/components/TrendCard/TrendCard';
import { TrendSummary } from 'src/components/TrendSummary/TrendSummary';
import { GemIconLabelled } from 'src/components/Header/Icons';

import agentStyles from 'src/components/AgentPanel/AgentActionGroups.styles';
import { media, style } from 'typestyle';
import { px } from 'csx';
import { infoPanelStyle } from 'src/common-ui/components/Macros/MacroDescription/MacroDescription';
import { MacrosProps } from 'src/common-ui/components/Macros/Macros.types';
import type { MasterDetailInsightsConfig } from 'src/components/MasterDetailRenderer/MasterDetail.types';
import { viewItemToFinancialProps } from 'src/pages/Hindsighting/CategoryRecap/CategorySummary/CategorySummary.selectors';
import { TenantConfigViewItem } from 'src/dao/tenantConfigClient';

export const quickTrendsStyles = (isPrintMode: boolean, numCardsLarge: number = 2) => ({
  summary: style(
    // 1 card width for small screens, 2 for larger screens
    media(
      { maxWidth: 1672 },
      {
        $debugName: 'quicktrends-summary-container',
        width: `calc(${px(calcTrendCardWidth() * 1)} + 2rem)`,
      },
    ),
    {
      $debugName: 'quicktrends-summary-container',
      width: `calc(${px(calcTrendCardWidth() * (isPrintMode ? 1 : numCardsLarge))} + 2rem)`,
      height: '100%',
    },
  ),
  details: style({
    $debugName: 'quicktrends-details-container',
    height: '100%',
    padding: '0 1rem 0 0',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  }),
});

interface MasterDetailInsightsProps {
  config: MasterDetailInsightsConfig;
  data: BasicPivotItem[];
}

export const MasterDetailInsights = ({ config, data }: MasterDetailInsightsProps) => {
  const isPrintMode = false;
  const numberCardsWide = 2;
  const styles = quickTrendsStyles(isPrintMode, numberCardsWide);
  const headerConfig = config.header;
  const trendSummaryConfig: TrendSummaryConfig = {
    type: 'trendCards',
    cards: config.metrics.left,
    numberCardsWide: 2,
    title: '',
    hideTitle: true,
  };

  const infoPanelConfig = config.metrics.right;
  const infoMacros: MacrosProps[] = infoPanelConfig.map((viewItem, index) =>
    viewItemToFinancialProps(viewItem as TenantConfigViewItem, data[index]),
  );

  return (
    <React.Fragment>
      {/* TODO: consolidate - Header design is taken directly from AgentAction.tsx */}
      <div className={styles.summary} data-qa="quicktrends-left">
        <header className={agentStyles.actionItem}>
          <div className={agentStyles.actionHeader}>
            <div className={agentStyles.actionIconContainer}>
              <i className={headerConfig.icon} />
            </div>
            <div className={agentStyles.actionTextContainer}>
              <div className={agentStyles.actionDescription}>{headerConfig.description}</div>
              <div className={agentStyles.actionTitle}>{headerConfig.text}</div>
            </div>
          </div>
        </header>
        <div style={{ height: '100%' }}>
          <TrendSummary config={trendSummaryConfig} data={data} />
        </div>
      </div>
      <Divider flexItem={true} orientation={'vertical'} />
      {/* TODO: consolidate - Info panel design is taken directly from MacroDescription.tsx */}
      <div
        style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', height: '100%', flex: 1 }}
      >
        {infoMacros.map((info, infoIndex) => {
          return (
            // remove `flex: 1` style so macros fill available space
            <div key={infoIndex} className={infoPanelStyle} style={{ flex: 'initial' }}>
              <div className="info-row">
                <div className="why-section">
                  <span className="why-header">{info.metrics.primary.label}</span>
                  <span className="why-value">{info.metrics.primary.rendered}</span>
                  <div className="powered-by-row">
                    <GemIconLabelled />
                  </div>
                </div>
                <div className="section-divider" />
                <ul className="descriptions-list">
                  {info.metrics.subMetrics.map((sm, i) => (
                    <li key={i}>{sm.rendered}</li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    </React.Fragment>
  );
};
